import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { unique } from '../../../../utils/collections';

const PromotionDialog = ({ selected, open, onClose, promoteEquipment, equipment }) => {
  const [loading, setLoading] = useState(false);
  const topics = useMemo(
    // Selected could contain entries that are no longer extant while dialog is not rendered
    () => unique(selected.flatMap((e) => equipment.get(e)?.points ?? []).map((p) => p.topic)),
    [equipment, selected]
  );
  if (!selected || selected.length <= 0) return null;

  const handlePromote = () => {
    setLoading(true);
    promoteEquipment(
      selected.map((eid) => equipment.get(eid)),
      topics
    )
      .then((result) => {
        const equipResults = Object.fromEntries(
          Object.entries(result.equipment).map(([equipId, equipResult]) => {
            const e = equipment.get(equipId);
            const points = Object.fromEntries(
              e.points.map((p) => [
                p.topic,
                {
                  status: (result.points[p.topic]?.length ?? 0) === 0 ? 'ok' : 'error',
                  message: result.points[p.topic],
                  point: p,
                },
              ])
            );
            let equipStatus = 'partial';
            if (equipResult.length > 0) {
              equipStatus = 'error';
            } else {
              const statuses = new Set(Object.values(points).map((p) => p.status));
              if (statuses.difference(new Set(['ok'])).size === 0) {
                equipStatus = 'ok';
              } else if (statuses.difference(new Set(['error'])).size === 0) {
                equipStatus = 'error';
              } // else partial
            }

            return [
              equipId,
              {
                points,
                status: equipStatus,
                equip: e,
                message: equipResult,
              },
            ];
          })
        );

        const statuses = unique(Object.values(equipResults).map((e) => e.status));
        let overallStatus = 'partial';
        if (statuses.length === 1 && statuses[0] === 'ok') {
          overallStatus = 'success';
        } else if (statuses.length === 1 && statuses[0] === 'error') {
          overallStatus = 'failure';
        } // else partial

        const closeResult = {
          status: overallStatus,
          equipment: equipResults,
        };
        setLoading(false);
        onClose(closeResult);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.log(`Promote failed for ${selected}: ${e}`);
        onClose({
          status: 'FAILED',
          equipment: [],
        });
        setLoading(false);
      });
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm Publish</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" component="label" htmlFor="myField">
          {selected.length} equipment / {topics.length} points will be published
        </Typography>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          onClick={() => onClose(undefined)}
          color="error"
          disabled={loading}
        >
          Abort
        </Button>
        <Button variant="contained" onClick={handlePromote} color="success" disabled={loading}>
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromotionDialog;
